import { useCallback } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { setDraggingProps } from '../../stores/actions/user-interface';
import {
	DropAreaData, DropCallback, DropCallbackData, RelativePosition,
} from '../../stores/reducers/user-interface';

interface useDropAreaProps {
	accepts: string | string[],
	dropCallback: DropCallback
	dropAreaData: DropAreaData // optional data the drop area can pass back w/ callback
}

const useDropArea = (props: useDropAreaProps) => {
	const { accepts, dropCallback, dropAreaData } = props;
	const dispatch = useDispatch();

	const calculateMouseOffSetFromDropArea = (
		monitor: DropTargetMonitor,
	): RelativePosition => {
		const relativePosition = { x: 10, y: 5 };
		const offset = monitor.getClientOffset();

		if (!offset || !dropAreaData?.id) {
			return relativePosition;
		}

		const dropAreaEl = document.getElementById(dropAreaData.id);
		const elPosData = dropAreaEl?.getBoundingClientRect();

		if (!elPosData) {
			return relativePosition;
		}

		const pxPosX = offset.x - elPosData.x;
		const pxPosY = offset.y - elPosData.y;
		relativePosition.x = (pxPosX / elPosData.width) * 100;
		relativePosition.y = (pxPosY / elPosData.height) * 100;

		return relativePosition;
	};

	const handleDrop = useCallback((
		item: { id: string | number, type: string },
		monitor: DropTargetMonitor,
	) => {
		const relativePosition = calculateMouseOffSetFromDropArea(monitor);
		const dropData: DropCallbackData = { ...dropAreaData, relativePosition };
		dispatch(setDraggingProps(null));
		dropCallback(item, dropData);
	}, [dropCallback, dropAreaData]);

	const [{ isOver }, dropRef] = useDrop(() => ({
		accept: accepts,
		drop: handleDrop,
		collect: (monitor: DropTargetMonitor) => ({
			isOver: monitor.isOver(),
		}),
	}), [handleDrop, accepts]);

	return {
		isOver,
		dropRef,
	};
};

export default useDropArea;
